"use client";
import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Dropdown, Flex, Modal, Typography } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { getCancelledDepositResultText } from "@utils/core";
import CancelAppointment, { type CancelledDeposit } from "./CancelAppointment";

interface ManageAppointmentButtonsProps {
  appointmentId: string;
  salonName?: string | null;
  salonPhoneNumber?: string | null;
  memberPhoneNumber?: string | null;
  memberName?: string | null;
  cancellationPolicy?: {
    hours?: number | null;
    note?: string | null;
    lateGraceMinutes?: number | null;
  };
  /** False when the business turned its cancellation window off. */
  cancellationPolicyEnabled?: boolean;
  /** ISO start of the appointment. */
  startsAt?: string;
  bookingHref?: string;
  bookingLabel?: string;
  /**
   * Dev/test seam only: mount straight into the post-cancel state so the
   * `/dev/manage-view` harness can show the confirmation without a real
   * cancel (which needs the API + an OTP). Production never sets this.
   */
  devInitialCanceled?: boolean;
}

const ManageAppointmentButtons: React.FC<ManageAppointmentButtonsProps> = ({
  appointmentId,
  salonName,
  salonPhoneNumber,
  memberPhoneNumber,
  memberName,
  cancellationPolicy,
  cancellationPolicyEnabled = true,
  startsAt,
  bookingHref = "/",
  bookingLabel = "Go to booking page",
  devInitialCanceled = false,
}) => {
  // modalRoute controls which modal content is shown; undefined means closed
  const [modalRoute, setModalRoute] = useState<
    "cancel" | "reschedule" | undefined
  >(undefined);

  // default to false so the appointment isn't shown as canceled immediately
  const [isCanceled, setIsCanceled] = useState<boolean>(devInitialCanceled);
  // What the cancel did with an upfront payment (post-OTP, so exact).
  const [depositResult, setDepositResult] = useState<string | null>(null);

  const openModal = useCallback(
    (route: "cancel" | "reschedule") => setModalRoute(route),
    [],
  );

  const closeModal = useCallback(() => setModalRoute(undefined), []);

  const handleCanceled = useCallback(
    (deposit?: CancelledDeposit) => {
      setDepositResult(getCancelledDepositResultText(deposit));
      setIsCanceled(true);
      closeModal();
    },
    [closeModal],
  );

  const modalOpen = modalRoute !== undefined;
  const modalTitle =
    modalRoute === "cancel"
      ? "Cancel Appointment"
      : modalRoute === "reschedule"
        ? "Reschedule Appointment"
        : undefined;

  const callOptions = useMemo(() => {
    const options: Array<{ key: string; label: string; phone: string }> = [];
    const trimmedSalonName = salonName?.trim();
    if (salonPhoneNumber) {
      options.push({
        key: "salon",
        label: trimmedSalonName ? `Call ${trimmedSalonName}` : "Call business",
        phone: salonPhoneNumber,
      });
    }
    // The API sends a member number only when the team member made it
    // public (`phonePublic`); without one the customer calls the business,
    // so no dead "Call <member>" entry is offered.
    if (memberPhoneNumber) {
      const trimmedMemberName = memberName?.trim();
      options.push({
        key: "member",
        label: trimmedMemberName
          ? `Call ${trimmedMemberName}`
          : "Call team member",
        phone: memberPhoneNumber,
      });
    }
    return options;
  }, [memberName, memberPhoneNumber, salonName, salonPhoneNumber]);

  const handleCallMenuClick = useCallback(
    ({ key }: { key: string }) => {
      const option = callOptions.find((entry) => entry.key === key);
      if (!option?.phone) return;
      window.location.href = `tel:${option.phone}`;
    },
    [callOptions],
  );

  if (isCanceled) {
    // The dialog's own toast never survives the close, so this card-level
    // confirmation is the customer's only "it worked" signal — make it read
    // as success, not as a warning that the buttons vanished.
    return (
      <Alert
        type="success"
        showIcon
        title="Your appointment has been canceled"
        description={
          depositResult ? (
            <>
              This visit is no longer on the schedule.
              <br />
              {depositResult}
            </>
          ) : (
            "This visit is no longer on the schedule."
          )
        }
      />
    );
  }

  return (
    <div>
      {/* Constructive actions first, destructive last (and quieter):
          most visits are reschedules — never lead with Cancel. */}
      <Flex wrap="wrap" gap={10}>
        <Button
          shape="round"
          onClick={() => openModal("reschedule")}
          aria-label="Reschedule appointment"
        >
          Reschedule
        </Button>

        <Dropdown
          menu={{
            items: callOptions.map((option) => ({
              key: option.key,
              label: option.label,
            })),
            onClick: handleCallMenuClick,
          }}
          disabled={callOptions.length === 0}
          trigger={["click"]}
        >
          <Button
            shape="round"
            disabled={callOptions.length === 0}
            aria-label="Call"
            icon={<DownOutlined />}
          >
            Call
          </Button>
        </Dropdown>

        <Button
          type="text"
          shape="round"
          onClick={() => openModal("cancel")}
          aria-label="Cancel appointment"
          style={{
            color:
              "var(--openings-booking-skin-text-secondary, var(--ant-color-text-secondary, #666))",
          }}
        >
          Cancel
        </Button>
      </Flex>

      <Modal
        // Portals to document.body: the class carries the skin's variables.
        rootClassName="openings-booking-portal"
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        title={modalTitle}
      >
        {modalRoute === "cancel" && (
          <CancelAppointment
            appointmentId={appointmentId}
            onCanceled={handleCanceled}
            cancellationPolicy={cancellationPolicy}
            cancellationPolicyEnabled={cancellationPolicyEnabled}
            startsAt={startsAt}
          />
        )}

        {modalRoute === "reschedule" && (
          <>
            {/* No automatic-cancel promise: the booking page only moves
                this visit when the customer takes the Reschedule branch,
                and only once the replacement commits
                (`booking-flow.instructions.md` § 8 Reschedule). */}
            <Typography.Paragraph>
              Pick a new time on the booking page. When you are asked whether
              to make another appointment or reschedule, choose Reschedule so
              this visit moves instead of a second one being added. Please
              avoid last-minute changes.
            </Typography.Paragraph>
            <Button type="primary" href={bookingHref} rel="noopener noreferrer">
              {bookingLabel}
            </Button>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ManageAppointmentButtons;
